import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import api, { CATEGORY_LABELS, BRANDS } from "../lib/api";
import Marquee from "../components/Marquee";
import ListingCard from "../components/ListingCard";
import Seo from "../components/Seo";

const HERO_LINES = ["ORIGINÁLNÍ", "ALU KOLA", "& AUTODÍLY"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const chapters = [
  { n: "01", t: "Prověřená kvalita", d: "Prodáváme pouze originální alu kola a pneumatiky s pozitivními referencemi. Každý kus prochází kontrolou." },
  { n: "02", t: "Skladem ihned", d: "Naše nabídka je pravidelně aktualizována a vždy máme skladem mnoho produktů připravených k předvedení." },
  { n: "03", t: "Rozvoz po Praze", d: "Pro maximální komfort zajišťujeme rozvoz zboží po celé Praze. Domluvíme termín, který vám vyhovuje." },
  { n: "04", t: "20+ let na trhu", d: "Na trhu působíme od roku 2002. Jsme odborníci ve svém oboru, připraveni poradit s výběrem." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/listings", { params: { featured: true } })
      .then((res) => setFeatured(res.data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div>
      <Seo />
      {/* HERO */}
      <section data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div
          className="absolute inset-0 z-0"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="https://images.unsplash.com/photo-1607908181896-65d5f06fd6c4?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000"
            alt="Detail alu kola"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-[1500px] mx-auto w-full px-5 md:px-10 pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-head uppercase tracking-[0.35em] text-xs text-[#ff3b30] mb-6"
          >
            M + J s.r.o. — od roku 2002
          </motion.div>

          <h1 className="font-display text-[12vw] leading-[0.96] md:text-[10vw] md:leading-[0.86] tracking-tight">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className={`block pt-[0.06em] md:pt-0 ${i === 1 ? "text-[#ff3b30]" : "text-white"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8"
          >
            <p className="text-white/70 text-base md:text-lg max-w-md leading-relaxed">
              Široký výběr originálních alu kol, pneu a autodílů pro Audi, VW, BMW, Škoda, Mercedes-Benz a další. Vše skladem, s rozvozem po Praze.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/katalog"
                data-testid="hero-cta-catalog"
                className="group inline-flex items-center gap-3 font-head uppercase tracking-wider bg-[#ff3b30] text-white px-7 py-4 hover:bg-white hover:text-black transition-colors duration-300"
              >
                Prohlédnout katalog
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="hero-cta-contact"
                className="inline-flex items-center font-head uppercase tracking-wider border border-white/25 px-7 py-4 hover:border-white transition-colors"
              >
                Poptávka
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* CATEGORIES */}
      <section className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {[
            { key: "kola", img: "https://images.pexels.com/photos/5965324/pexels-photo-5965324.jpeg" },
            { key: "kusovky", img: "https://images.unsplash.com/photo-1624750035364-165b03a3089f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
            { key: "pneu", img: "https://images.unsplash.com/photo-1760836395760-9831facb4754?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
            { key: "poklicky", img: "https://images.unsplash.com/photo-1668639235092-301730d1b72e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
            { key: "dily", img: "https://images.unsplash.com/photo-1676288176903-a68732722cce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
          ].map((c, i) => (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link
                to={`/katalog?category=${c.key}`}
                data-testid={`category-tile-${c.key}`}
                className="group relative flex flex-col justify-end h-[380px] md:h-[460px] overflow-hidden bg-[#0d0d0d]"
              >
                <img src={c.img} alt={CATEGORY_LABELS[c.key]} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <div className="relative p-7">
                  <div className="font-head uppercase tracking-[0.2em] text-[11px] text-[#ff3b30] mb-3">0{i + 1}</div>
                  <div className="font-display text-3xl md:text-4xl mb-2">{CATEGORY_LABELS[c.key]}</div>
                  <span className="inline-flex items-center gap-2 text-white/60 text-sm group-hover:text-white transition-colors">
                    Zobrazit <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BRANDS — vyberte značku */}
      <section data-testid="brands-section" className="relative z-10 border-t border-white/10 bg-[#080808]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="font-head uppercase tracking-[0.3em] text-xs text-[#ff3b30] mb-4">Hledáte konkrétní značku?</div>
              <h2 className="font-display text-4xl md:text-6xl leading-none">VYBERTE ZNAČKU</h2>
            </div>
            <p className="text-white/50 max-w-sm text-sm">
              Klikněte na značku svého vozu a zobrazíme vám všechna dostupná alu kola, pneu i díly pro daný vůz.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {BRANDS.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={`/katalog?brand=${encodeURIComponent(b)}`}
                  data-testid={`brand-tile-${b}`}
                  className="group flex items-center justify-between h-28 md:h-32 px-6 md:px-8 bg-[#0d0d0d] hover:bg-[#141414] transition-colors"
                >
                  <span className="font-display text-xl md:text-2xl text-white/70 group-hover:text-white transition-colors">{b}</span>
                  <ArrowRight size={18} className="text-white/20 group-hover:text-[#ff3b30] group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="font-head uppercase tracking-[0.3em] text-xs text-[#ff3b30] mb-4">Vybrané inzeráty</div>
              <h2 className="font-display text-4xl md:text-6xl leading-none">DOPORUČUJEME</h2>
            </div>
            <Link to="/katalog" className="hidden md:inline-flex items-center gap-2 font-head uppercase tracking-wider text-sm text-white/70 hover:text-white transition-colors">
              Vše <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section data-testid="manifesto" className="relative z-10 border-t border-white/10 bg-[#080808]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <h2 className="font-display text-4xl md:text-6xl leading-[0.9] sticky top-28">
                PROČ<br />NAKUPOVAT<br /><span className="text-[#ff3b30]">U NÁS</span>
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {chapters.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="bg-[#080808] p-8 md:p-10"
                >
                  <div className="font-display text-5xl text-white/15 mb-6">{c.n}</div>
                  <div className="flex items-center gap-2 mb-3">
                    <Check size={18} className="text-[#ff3b30]" />
                    <h3 className="font-head text-xl font-semibold">{c.t}</h3>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">{c.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32 text-left">
        <div className="border border-white/10 bg-gradient-to-br from-[#150404] to-[#0d0d0d] p-10 md:p-20">
          <div className="font-head uppercase tracking-[0.3em] text-xs text-[#ff3b30] mb-6">Nenašli jste, co hledáte?</div>
          <h2 className="font-display text-4xl md:text-7xl leading-[0.9] max-w-3xl mb-8">
            NAPIŠTE NÁM POPTÁVKU
          </h2>
          <p className="text-white/60 max-w-xl mb-10">
            Sháníte konkrétní sadu kol nebo vozidlo? Ozvěte se — máme rozsáhlý sklad a poradíme s výběrem.
          </p>
          <Link to="/kontakt" data-testid="cta-contact-button" className="group inline-flex items-center gap-3 font-head uppercase tracking-wider bg-[#ff3b30] text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300">
            Poslat poptávku <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
